import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import type { ClientTabPath } from "@ferrivia/analytics";
import type { Market } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useMarket } from "@ferrivia/i18n";
import { tabsFor } from "./routes";
import {
  HOME,
  TabNavigationContext,
  type AppHistoryState,
  type TabNavigationValue,
  type TabNavMethod,
} from "./useTabNavigation";

function isTabPath(path: string, market: Market): path is ClientTabPath {
  return tabsFor(market).some((tab) => tab.path === path);
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/* Watches the router's location and reports every tab change from one place,
   rather than from the tab bar's click handler. Two reasons: a hardware back
   press has no click handler to hang off at all, and a navigation added
   somewhere else later (the home surface's chat blocks are the current
   example) gets counted without anyone remembering to instrument it.

   The method is what the app declared for this navigation if it declared one,
   and otherwise inferred: a POP nobody asked for is the user's own back
   gesture, and anything else is a stray in-app navigation that did not go
   through `goToTab`. The inference is the safety net — a future
   `navigate("/chat")` written without the hook reports honestly instead of
   being filed as a back press. */
function useTabChangeAnalytics(pendingMethod: React.RefObject<TabNavMethod | null>): void {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const tracker = useTracker();
  const market = useMarket();
  const previousPath = useRef(pathname);

  useEffect(() => {
    const from = previousPath.current;
    if (from === pathname) return;
    previousPath.current = pathname;

    // Ignores the /foo -> / redirect TabPanels does for an unknown path:
    // that is a correction, not a tab change the user made.
    if (!isTabPath(from, market) || !isTabPath(pathname, market)) return;

    const method = pendingMethod.current ?? (navigationType === "POP" ? "back" : "in_app_link");
    pendingMethod.current = null;
    tracker.track("nav.tab_changed", { from, to: pathname, method });
  }, [market, navigationType, pathname, pendingMethod, tracker]);
}

/**
 * Owns how the client app moves between tabs, so the browser's own back
 * behaves the way a native tab bar's does.
 *
 * **Home is the start destination.** Back from any tab returns to home; back
 * from home leaves the app. That is Material's start-destination rule, and it
 * matches iOS, where back within a tab bar does not exist at all. What makes
 * it work is the *shape of the history stack* rather than any interception:
 * these rules keep it at `[/]` or `[/, <tab>]` and never deeper, so the
 * browser's unmodified back button already does the right thing. No
 * `popstate` listener, and — because every move goes through the router — it
 * behaves identically under `MemoryRouter` in tests.
 *
 *   from home        push, marking the entry `fromHome`
 *   to home, marked  pop, which unwinds the push above
 *   to home, unmarked  replace (deep-linked straight into a tab: nothing to
 *                      pop to, and pushing home would make back go *forwards*
 *                      into the tab the user just left)
 *   tab to tab       replace, carrying `fromHome` across so a later tap on
 *                    home still pops instead of stacking
 */
export function TabNavigationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Set immediately before navigating and consumed by the effect that sees
  // the resulting location. A ref, not state: it must not cause a render, and
  // it has to survive the navigation that reads it — including `navigate(-1)`,
  // which arrives asynchronously as a POP.
  const pendingMethod = useRef<TabNavMethod | null>(null);
  // Panels keep their own scroll position across a tab round trip (see
  // TabPanels), which is what makes them worth reaching back into.
  const panels = useRef(new Map<ClientTabPath, HTMLElement>());
  const tracker = useTracker();

  useTabChangeAnalytics(pendingMethod);

  const registerPanel = useCallback((path: ClientTabPath, element: HTMLElement | null) => {
    if (element) panels.current.set(path, element);
    else panels.current.delete(path);
  }, []);

  /* Tapping the tab already showing scrolls it back to the top — an iOS
     convention, and the fastest way out of a long panel.

     `scrollTop` decides whether anything happens, which also settles the chat
     tab without naming it: that panel is deliberately not scrollable (its feed
     scrolls inside ThreadView), so its scrollTop is always 0 and this does
     nothing. That is the behaviour we want anyway — a thread is anchored to
     its newest message, and no messaging app throws you to the oldest one. */
  const scrollPanelToTop = useCallback(
    (path: ClientTabPath) => {
      const panel = panels.current.get(path);
      if (!panel || panel.scrollTop === 0) return;
      panel.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
      tracker.track("nav.tab_reselected", { tab: path });
    },
    [tracker],
  );

  const { pathname } = location;
  const goToTab = useCallback(
    (target: ClientTabPath, method: TabNavMethod) => {
      if (target === pathname) {
        scrollPanelToTop(target);
        return;
      }

      pendingMethod.current = method;
      const fromHome = (location.state as AppHistoryState | null)?.fromHome === true;

      if (pathname === HOME) {
        navigate(target, { state: { fromHome: true } satisfies AppHistoryState });
      } else if (target === HOME) {
        if (fromHome) navigate(-1);
        else navigate(HOME, { replace: true });
      } else {
        navigate(target, { replace: true, state: { fromHome } satisfies AppHistoryState });
      }
    },
    [location.state, navigate, pathname, scrollPanelToTop],
  );

  const value = useMemo<TabNavigationValue>(() => ({ goToTab, registerPanel }), [goToTab, registerPanel]);

  return <TabNavigationContext.Provider value={value}>{children}</TabNavigationContext.Provider>;
}
